"use strict";

const Steve = require("@gluenetworks/steve_core").getInstance();
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./EntityGridRenderer");
const LinesNumberComponent = require("./utils/lines_number/LinesNumberComponent");
const ColumnSettingsComponent = require("./utils/column_settings/ColumnSettingsComponent");
const GroupingComponent = require("./utils/grouping");
const FilterComponent = require("./utils/filter");
const AdvancedFilter = require("./utils/advanced_filter");
const _ = require("lodash");

const MAX_GROUPS = 200;

class EntityGridComponent extends EventEmitter {
    constructor(configOptions) {
        super();

        this._storageFactory = Steve.di.getRegistered("$teve.storageFactory").registeredObj;

        this.setMaxListeners(0);

        const colConfig = _.get(configOptions, "columns", {});
        this._id = _.get(configOptions, "id", "entity_list_grid_gl_" + Date.now());
        const additionalFilter = _.get(configOptions, "additionalFilter", []);
        this._pagingFeed = _.get(configOptions, "pagingFeed");
        this._logger = _.get(configOptions, "logger", console);
        this._modalUserFeedback = _.get(configOptions, "modalUserFeedback");
        this._MAX_GROUPS = _.get(configOptions, "maxGroups", MAX_GROUPS);
        this._allowDragNDrop = _.get(configOptions, "allowDragNDrop", false);

        this._externalFilter = _.get(configOptions, "externalFilter", undefined);

        let urlParams = {};
        if (!_.isEmpty(Steve.router.routeData)) {
            urlParams = Steve.router.routeData;
        }
        this._resetUrlParams();

        // Non clickable rows
        this._nonClickableRows = _.get(configOptions, "nonClickableRows", false);

        this._groupBy = undefined;
        this.groups = [];
        this.expandedGroups = [];
        this._groupingValuesFeed = _.get(configOptions, "groupingValuesFeed");
        this._groupingRefreshGen = 0;
        this._columnsTypeDataByFieldPath = this._pagingFeed.getColumnsData().reduce(
            (obj, col) =>
                Object.assign({}, obj, {
                    [col.name]: col,
                }),
            {}
        );

        // useUserDataStorage boolean deprecated and replaced by userGridData
        // const useUserDataStorage = _.get(configOptions, 'useUserDataStorage', true);

        this._gridKey = _.get(configOptions, "gridKey");
        this._userGridData = _.get(configOptions, "userGridData");
        //if (this._userGridData) {
        // If UserData instance already been created and passed via userGridData, only pass grid model for event listeners
        //this._userGridData.setGridModel(this);
        //this._groupBy = _.get(this._userGridData.getGroupingData(), 'groupBy');
        //} else {
        this._userGridData = this._storageFactory.getRemoteStorageInstanceForEntityGrid(
            "entity_grid_gl",
            this._gridKey
        ); //new UserGridData(this._gridKey, this);
        this._userGridData.setGridModel(this);

        let sortFromUrl = {};
        if (_.get(urlParams, "sort", false)) {
            sortFromUrl = _.get(urlParams, "sort", {});
        }
        this._columns = this._applySortingDataToColumns(
            colConfig,
            sortFromUrl || this._pagingFeed.getSort()
        );

        // Initialise all column related properties with default columnConfig data
        this._lockedColumnsCache = [];
        this._lockedRightColumnsCache = [];
        let columnOrder = _.get(
            configOptions,
            "columnOrder",
            this.getInitialColumnOrder(colConfig)
        );
        this._initColumnOrder(columnOrder);
        this._updateColumnOrderCache();

        this._showHeader = _.get(configOptions, "showHeader", true);
        this._selectedRows =
            _.get(urlParams, "selection", false) || _.get(configOptions, "selectedRows", []);
        this._singleSelect = _.get(configOptions, "singleSelect", false);
        this._selectable = _.get(configOptions, "selectable", true);
        this._showLinesNumber = _.get(configOptions, "showLinesNumber", false);

        if (this._showLinesNumber) {
            this._linesNumberCmpnt = new LinesNumberComponent(this, this._logger);
            if (this._showLinesNumber instanceof Element) {
                this._showLinesNumber.appendChild(this._linesNumberCmpnt.renderer.rootElement);
            }
        }

        const columnSettingsWidgetContainer = _.get(configOptions, "columnSettingsWidgetContainer");
        if (columnSettingsWidgetContainer) {
            this._columnSettingsCmpnt = new ColumnSettingsComponent(this._logger);
            columnSettingsWidgetContainer.appendChild(
                this._columnSettingsCmpnt.renderer.rootElement
            );
        }

        const groupingWidgetContainer = _.get(configOptions, "groupingContainer");
        if (groupingWidgetContainer) {
            //const storedGroupingData = this._userGridData.getGroupingData();
            this._groupingCmpnt = new GroupingComponent({
                logger: this._logger,
                entityGrid: this /*, groupBy: storedGroupingData.groupBy*/,
            });
            groupingWidgetContainer.appendChild(this._groupingCmpnt.getRootElement());
        }

        const filterComponent = _.get(configOptions, "filterComponent");
        if (filterComponent) {
            const storedBasicSearchUserData = _.get(
                this._userGridData.getBasicSearchData(),
                "filterData",
                {}
            );
            const storedAdvancedSearchUserData =
                _.get(urlParams, "filter", false) || this._userGridData.getAdvancedSearchData();
            this._filterComponent = new FilterComponent(
                this,
                this._pagingFeed.getColumnsData(),
                this._logger,
                storedBasicSearchUserData,
                storedAdvancedSearchUserData
            );
            if (filterComponent instanceof Element) {
                filterComponent.appendChild(this._filterComponent.getRootElement());
            }
        }

        const advancedFilter = _.get(configOptions, "advancedFilterComponent");
        if (advancedFilter) {
            let columnConfigWithAdditionalFilter = _.keys(this._pagingFeed._entityFields);
            if (!_.isEmpty(additionalFilter)) {
                columnConfigWithAdditionalFilter = _.concat(
                    _.keys(this._pagingFeed._entityFields),
                    additionalFilter.map((filter) => filter.name)
                );
            }
            const storedAdvancedSearchUserData = this._clearFilter(
                _.get(urlParams, "filter", false) || this._userGridData.getAdvancedSearchData(),
                columnConfigWithAdditionalFilter
            );

            if (
                !_.isEmpty(storedAdvancedSearchUserData) &&
                _.isEmpty(this._pagingFeed.getFilterData())
            ) {
                this._pagingFeed.setFilter(storedAdvancedSearchUserData);
            }

            this._advancedFilter = new AdvancedFilter(this, {
                filterData: storedAdvancedSearchUserData,
                columnsData: this._pagingFeed.getColumnsData(),
                additionalFilter: additionalFilter,
            });
            if (advancedFilter instanceof Element) {
                filterComponent.appendChild(this._advancedFilter.getRootElement());
            }
        }

        this._groupingFieldFromUrl = undefined;
        if (!_.isUndefined(urlParams.groupingField)) {
            this._groupingFieldFromUrl = _.get(urlParams, "groupingField", []);
            if (!_.isEmpty(this._groupingFieldFromUrl)) {
                this.setGroupBy(this._groupingFieldFromUrl);
            } else {
                this.setGroupBy();
            }
        }

        this._entities = {};

        const pagingFeedQuery = this._pagingFeed.getFilterData();
        this._filterQuery = _.get(pagingFeedQuery, "$userQuery", pagingFeedQuery || {});
        // set filterQuery to {} if it is same as external filter
        if (!_.isEmpty(this._externalFilter)) {
            if (_.isEqual(this._filterQuery, this._externalFilter)) {
                this._filterQuery = {};
            } else {
                if (this._filterQuery.$and) {
                    // get all filters/rules that are not same as external filter
                    const nonExtFilter = this._filterQuery.$and.filter((query) => {
                        return !_.isEqual(query, this._externalFilter);
                    });
                    // length of rules/filters not same is external filter set filterQuery to {}
                    // else set filterQuery to filtered list
                    if (nonExtFilter.length === 0) {
                        this._filterQuery = {};
                    } else {
                        this._filterQuery.$and = nonExtFilter;
                    }
                }
            }
        }
        this._renderer = new Renderer(this);
        this._registerListeners();

        //window.EntityGridComponent = this;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerUserDataListeners() {
        if (!this._userGridData) {
            return;
        }
        //userData listeners
        this._userGridData.on(
            "widthChanged",
            (this._onStorageWidthChanged = this._onStorageWidthChanged.bind(this))
        );
        this._userGridData.on(
            "orderChanged",
            (this._onStorageOrderChanged = this._onStorageOrderChanged.bind(this))
        );
        this._userGridData.on(
            "lockedChanged",
            (this._onStorageLockedChanged = this._onStorageLockedChanged.bind(this))
        );
        this._userGridData.on(
            "sortingChanged",
            (this._onStorageSortingChanged = this._onStorageSortingChanged.bind(this))
        );
    }

    _registerListeners() {
        this._updateColumnSettings = this._updateColumnSettings.bind(this);

        this._pagingFeed.on("RefreshView", (this._onRefreshView = this._onRefreshView.bind(this)));
        this._pagingFeed.on(
            "FeedReestablished",
            (this._onFeedReestablished = this._onFeedReestablished.bind(this))
        );
        this._pagingFeed.on(
            "RefreshLinesNumber",
            (this._onFeedRefreshLinesNumber = this._onFeedRefreshLinesNumber.bind(this))
        );

        if (this._columnSettingsCmpnt) {
            this._columnSettingsCmpnt.on(
                "ColumnSettingsChange",
                (this._onColumnSettingsChanged = this._onColumnSettingsChanged.bind(this))
            );
            this.on("UserReordered", this._updateColumnSettings);
            this.on("LockedColumns", this._updateColumnSettings);
        }

        if (this._groupingCmpnt) {
            this._groupingCmpnt.on(
                "UserChangeGrouping",
                (this._onUserChangeGrouping = this._onUserChangeGrouping.bind(this))
            );
        }

        if (this._filterComponent) {
            this._filterComponent.on(
                "BasicSearchFilterChange",
                (this._onBaseFilterUserChanged = this._onBaseFilterUserChanged.bind(this))
            );
        }
        if (this._advancedFilter) {
            this._advancedFilter.on(
                "AdvancedFilterChange",
                (this._onAdvancedFilterUserChanged = this._onAdvancedFilterUserChanged.bind(this))
            );
        }

        this.on("UserSorted", (this._onUserSorted = this._onUserSorted.bind(this))); //show loading veil on resort

        this._registerUserDataListeners();

        if (this._groupingValuesFeed) {
            this._registerGroupingFeedListeners();
        }
    }

    _unregisterUserDataListeners() {
        if (!this._userGridData) {
            return;
        }
        this._userGridData.removeListener("widthChanged", this._onStorageWidthChanged);
        this._userGridData.removeListener("orderChanged", this._onStorageOrderChanged);
        this._userGridData.removeListener("lockedChanged", this._onStorageLockedChanged);
        this._userGridData.removeListener("sortingChanged", this._onStorageSortingChanged);
    }

    _unregisterListeners() {
        this._pagingFeed.removeListener("RefreshView", this._onRefreshView);
        this._pagingFeed.removeListener("FeedReestablished", this._onFeedReestablished);
        this._pagingFeed.removeListener("RefreshLinesNumber", this._onFeedRefreshLinesNumber);

        if (this._columnSettingsCmpnt) {
            this._columnSettingsCmpnt.removeListener(
                "ColumnSettingsChange",
                this._onColumnSettingsChanged
            );
            this.removeListener("UserReordered", this._updateColumnSettings);
            this.removeListener("LockedColumns", this._updateColumnSettings);
        }

        if (this._groupingCmpnt) {
            this._groupingCmpnt.removeListener("UserChangeGrouping", this._onUserChangeGrouping);
        }

        if (this._filterComponent) {
            this._filterComponent.removeListener(
                "BasicSearchFilterChange",
                this._onBaseFilterUserChanged
            );
        }

        if (this._advancedFilter) {
            this._filterComponent.removeListener(
                "AdvancedFilterChange",
                (this._onAdvancedFilterUserChanged = this._onAdvancedFilterUserChanged.bind(this))
            );
        }

        this.removeListener("UserSorted", this._onUserSorted);

        this._unregisterUserDataListeners();

        if (this._groupingValuesFeed) {
            this._unregisterGroupingFeedListeners();
        }
    }

    _registerGroupingFeedListeners() {
        this._onGroupValueUpdated = this._onGroupValueUpdated.bind(this);
        this._onGroupValuesChanged = this._onGroupValuesChanged.bind(this);
        // Leading edge so a one-shot bulk change lands immediately, maxWait so a long running
        // one keeps converging instead of waiting for it to finish.
        this._refreshGroupingValuesDebounced = _.debounce(
            () => this._refreshGroupingValues(),
            500,
            {
                leading: true,
                maxWait: 5000,
            }
        );
        // A re-init replays the whole map as per value events, so collapse the structural ones
        // into one rebuild instead of one per changed group.
        this._applyGroupingValuesChangeDebounced = _.debounce(
            () => this._applyGroupingValuesChange(),
            0
        );

        // The view renderers own the feed, so it can be torn down before the grid is. Without
        // this a trailing refresh rejects with "feed is inactive" and warns during teardown.
        this._onGroupingFeedShutdown = () => {
            this._refreshGroupingValuesDebounced.cancel();
            this._applyGroupingValuesChangeDebounced.cancel();
        };

        this._groupingValuesFeed.addListener("ValueUpdated", this._onGroupValueUpdated);
        this._groupingValuesFeed.addListener(
            "ValueAdded",
            this._applyGroupingValuesChangeDebounced
        );
        this._groupingValuesFeed.addListener(
            "ValueDeleted",
            this._applyGroupingValuesChangeDebounced
        );
        this._groupingValuesFeed.addListener("Repage", this._refreshGroupingValuesDebounced);
        this._groupingValuesFeed.addListener("Shutdown", this._onGroupingFeedShutdown);
    }

    _unregisterGroupingFeedListeners() {
        this._groupingValuesFeed.removeListener("ValueUpdated", this._onGroupValueUpdated);
        this._groupingValuesFeed.removeListener(
            "ValueAdded",
            this._applyGroupingValuesChangeDebounced
        );
        this._groupingValuesFeed.removeListener(
            "ValueDeleted",
            this._applyGroupingValuesChangeDebounced
        );
        this._groupingValuesFeed.removeListener("Repage", this._refreshGroupingValuesDebounced);
        this._groupingValuesFeed.removeListener("Shutdown", this._onGroupingFeedShutdown);
        this._refreshGroupingValuesDebounced.cancel();
        this._applyGroupingValuesChangeDebounced.cancel();
    }
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenModelIsReady() {
        return Promise.resolve().then(() => {
            // Update all column related properties with saved user preferences
            const userDataColumnOrder = this._userGridData.getColOrder();

            this._updateColumnsVisibility(userDataColumnOrder); // Silently refreshes this._columns with shown boolean based on saved user preferences
            this.setColumnOrder(userDataColumnOrder); // Updates _columnOrder and _columnOrderCache with saved user preferences
            this.setLockedColumns(this._userGridData.getLocked(), true); // Silently updates .locked property of this._columns and then _columnsCache
            this._updateColumnSettings(); // Update column settings
        });
    }

    get pagingFeed() {
        return this._pagingFeed;
    }

    get renderer() {
        return this._renderer;
    }

    getRenderer() {
        return this._renderer;
    }

    get gridIsUnSelectable() {
        return !this._selectable;
    }

    resetScroll() {
        this.renderer._gridBodyContainer.scrollTop = 0;
    }

    get groupedBy() {
        return this._groupBy;
    }

    shutdown() {
        const userDataShutdown = this._userGridData
            ? this._userGridData.shutdown()
            : Promise.resolve();
        this._unregisterListeners();
        return Promise.all([this._renderer._shutdown(), userDataShutdown])
            .then(() => {
                this._pagingFeed = undefined;
                this._logger = undefined;
                this._gridKey = undefined;
                this._lockedColumnsCache = [];
                this._lockedRightColumnsCache = [];
                this._showLinesNumber = undefined;
                this._filterComponent = undefined;
                this._nonClickableRows = undefined;
                this._renderer = undefined;
                this._userGridData = undefined;
                this._id = undefined;
                this._entities = undefined;
                this._columns = undefined;
                this._showHeader = undefined;
                this._columnOrder = undefined;
                this._columnOrderCache = undefined;
                this._selectedRows = undefined;
                this._singleSelect = undefined;
                this._selectable = undefined;
                this._linesNumberCmpnt = undefined;
                this._columnSettingsCmpnt = undefined;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    `EntityGridComponent.shutdown error (${this._gridKey || ""})`
                );
            });
    }

    setExternalFilter(filter, silent = false) {
        this._externalFilter = filter;
        this.updateFiltering(silent);
    }

    clearExternalFilter(silent = false) {
        this._externalFilter = undefined;
        this.updateFiltering(silent);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onRefreshView() {
        this.refreshView();
        this.setBusyVeil(false);
    }

    _onFeedReestablished() {
        this.resetScroll();
        this.renderer.deleteAllRows();
        //this.setBusyVeil(false);
    }

    _onFeedRefreshLinesNumber() {
        this.refreshLinesNumber();
    }

    _onStorageWidthChanged(data) {
        this._updateColumnsWidth(data);
    }

    _onStorageOrderChanged(data) {
        const colsShouldBeHidden = this._columnOrderCache.filter(
            (colName) => !data.includes(colName)
        );
        const colsShouldBeShown = data.filter(
            (colName) => !this._columnOrderCache.includes(colName)
        );
        colsShouldBeHidden.forEach(this.hideColumn, this);
        colsShouldBeShown.forEach(this.showColumn, this);
        this.setColumnOrder(data);
        this._updateColumnSettings();
    }

    _onStorageLockedChanged(data) {
        this.setLockedColumns(data, false);
    }

    _onStorageSortingChanged(data) {
        const sortCriteria = Object.keys(data)[0]; //get primary sorting criteria
        const colConfig = this.getColumns();
        const sortDirection = data[sortCriteria] === -1 ? "desc" : "asc";

        const columnName = Object.keys(colConfig).find(
            (colId) => sortCriteria === _.get(colConfig, [colId, "path"], colId)
        );

        if (columnName) {
            this.renderer._setGridSorted(columnName, sortDirection);
        }
    }

    _onColumnSettingsChanged(columnSettings) {
        if (columnSettings instanceof Array) {
            let columnOrder = [];
            columnSettings.forEach((column) => {
                if (!column.hasOwnProperty("shown") || column.shown) {
                    this.showColumn(column.value);
                    columnOrder.push(column.value);
                } else {
                    this.hideColumn(column.value);
                }
            });
            // rely on correct order in columnSettings data - it should be sorted by priority
            this.setColumnOrder(columnOrder);
            //this._model.saveUserSettingsToStorage();
        } else {
            console.error("Column Settings should be an array");
        }
        this._triggerColumnSettingChange(columnSettings);
    }

    rowsClickable(entity) {
        if (!this._nonClickableRows) {
            return true;
        }

        const rules = _.keys(this._nonClickableRows);
        let isClickable = true;
        rules.map((eachRule) => {
            if (this._nonClickableRows[eachRule].indexOf(_.get(entity, `${eachRule}`, "")) > -1) {
                isClickable = false;
                return false;
            }
        });
        return isClickable;
    }

    _onUserChangeGrouping(groupBy, prevGroupBy) {
        this.groups = [];
        this.expandedGroups = [];
        this.setGroupBy(groupBy, [], prevGroupBy);
    }

    _onGroupValueUpdated(groupHeader, count) {
        const groupRow = _.get(this._renderer, ["_rowComponents", groupHeader]);
        if (groupRow && groupRow.getEntity()) {
            groupRow.getEntity().setValue("count", count);
        }
    }
    /**
     * Repaints the count on every group header currently rendered. Needed after a refresh because
     * EntityGridRenderer reuses an existing group row without re-reading getValueCount().
     * @param {DistinctValuesFeed} feed
     * @private
     */
    _syncRenderedGroupCounts(feed) {
        _.forEach(feed.getValuesCounts(), (count, groupHeader) =>
            this._onGroupValueUpdated(groupHeader, count)
        );
    }

    _onGroupValuesChanged(groupName, groups) {
        this.groups = this.convertDistinctValuesToDataTypedValues(
            this.getColumnValueDataType(this._groupBy),
            groups
        );
        this.refreshView();
    }

    _onUserSorted() {
        this.setBusyVeil(true);
        this.pagingFeed.once("FeedReestablished", () => this.setBusyVeil(false));
    }

    _onBaseFilterUserChanged(filterData, filterQuery) {
        this._onFilterDataChange(filterQuery);
        this._triggerBaseFilterChangeData({filterData, filterQuery});
    }
    _onAdvancedFilterUserChanged(filterQuery, {silent} = {}) {
        if (!silent) {
            this._onFilterDataChange(filterQuery);
        }
        this._triggerAdvancedFilterChangeData(filterQuery);
    }

    _onFilterDataChange(filterQuery, silent = false) {
        let _filterQuery = _.cloneDeep(filterQuery) || {};
        this._filterQuery = _.cloneDeep(filterQuery);
        const expandedGroups = _.uniq(this.expandedGroups);
        const replacementValue =
            (this._groupingValuesFeed && _.get(this._groupingValuesFeed, "_replacementValue")) ||
            null;

        if (expandedGroups.indexOf(replacementValue) > -1) {
            expandedGroups.concat([null, undefined]);
        }

        if (this._groupBy) {
            const groupByPath = this.getColumnValuePath(this._groupBy);

            //regular group value -> `{field: {$in: [value1, value2, value3]}}`
            //null group value -> `{field: {$type: 10}}`
            //undefined grout value -> `{field: {$exists: false}}`
            let groupingSubQueries = expandedGroups
                .reduce(
                    ([_in, _null, _notExists], value) => {
                        if (value === null) {
                            return [_in, {$type: 10}, _notExists];
                        }
                        if (value === undefined) {
                            return [_in, _null, {$exists: false}];
                        }
                        if (value === replacementValue) {
                            return [
                                {$in: _in.$in.concat([value, "", null, undefined])},
                                _null,
                                _notExists,
                            ];
                        }
                        return [{$in: _in.$in.concat(value)}, _null, _notExists];
                    },
                    [{$in: []}]
                )
                .filter(Boolean)
                .map((subQuery) => ({[groupByPath]: subQuery}));

            const groupingQueryPart =
                groupingSubQueries.length > 1 ? {$or: groupingSubQueries} : groupingSubQueries[0];

            if (
                _.isArray(_.get(groupingQueryPart, `${this._groupBy}.$in`)) &&
                _.get(groupingQueryPart, `${this._groupBy}.$in`).indexOf(replacementValue) > -1
            ) {
                const newGroup = _.concat(_.get(groupingQueryPart, `${this._groupBy}.$in`), [
                    null,
                    undefined,
                ]);
                _.set(groupingQueryPart, `${this._groupBy}.$in`, newGroup);
            }

            _filterQuery = {
                $and: [
                    _filterQuery,
                    groupingQueryPart, //{[groupByPath]: {$in: []}} if this is no expanded groups
                ],
            };
        }

        if (!_.isEmpty(this._externalFilter)) {
            _filterQuery = this._addExternalFilter(_filterQuery);
        }
        if (!silent) {
            this.setSelectedRows(null);
            this._refreshGroupingValues();
        }
        this.pagingFeed.setFilter(_filterQuery, silent);
    }

    /**
     * Builds the mongo query for the grouping feed. That feed is deliberately not constrained by
     * which groups are expanded, since it needs a count for every group.
     * @returns {Object}
     * @private
     */
    _buildGroupingValuesFeedQuery() {
        const systemPayload = _.pick(this.pagingFeed.getFilterData(), [
            "$systemQuery",
            "$schemeQuery",
        ]);
        const userQuery = _.isEmpty(this._externalFilter)
            ? this._filterQuery
            : this._addExternalFilter(this._filterQuery);

        return Object.assign(systemPayload, {$userQuery: userQuery});
    }

    /**
     * Reestablishes the grouping feed and pushes the result into the rendered grid, leaving scroll
     * position and expand state alone. Both a user filter change and a server repage land here.
     * @returns {Promise}
     * @private
     */
    _refreshGroupingValues() {
        if (!this._groupBy || !this._groupingValuesFeed || !this._renderer) {
            return Promise.resolve();
        }
        // Stops an older refresh from overwriting a newer one.
        const generation = ++this._groupingRefreshGen;

        return this._groupingValuesFeed
            .reestablishFeed(this._buildGroupingValuesFeedQuery())
            .then(
                () => {
                    if (generation !== this._groupingRefreshGen || !this._renderer) {
                        return;
                    }
                    // The reestablish lands as a re-init on its own, so go through the same
                    // coalesced handler and let the two collapse into one rebuild.
                    this._applyGroupingValuesChangeDebounced();
                },
                (error) => {
                    if (this._logger) {
                        this._logger.warn({error}, "groupingValuesFeed reestablish failed");
                    }
                }
            )
            .catch((error) => {
                if (this._logger) {
                    this._logger.warn({error}, "groupingValuesFeed refresh failed");
                }
            });
    }

    /**
     * Rebuilds group rows only when the set of groups actually changed, then repaints the counts.
     * Coalesced, so a re-init touching many groups costs one rebuild instead of one per group.
     * @private
     */
    _applyGroupingValuesChange() {
        const feed = this._groupingValuesFeed;
        if (!this._groupBy || !feed || !this._renderer) {
            return;
        }
        const values = feed.getValues();
        const groups = this.convertDistinctValuesToDataTypedValues(
            this.getColumnValueDataType(this._groupBy),
            values
        );
        // Counts moving between existing groups need no row rebuild, so skip refreshView()
        // and just repaint the counts.
        if (_.xor(groups, this.groups).length) {
            this._onGroupValuesChanged(null, values);
        }
        this._syncRenderedGroupCounts(feed);
    }

    _addExternalFilter(filterQuery) {
        let _filterQuery = _.cloneDeep(filterQuery);
        if (_.isEmpty(_filterQuery)) {
            _filterQuery = this._externalFilter;
            return _filterQuery;
        }
        if (_filterQuery.$and) {
            _filterQuery.$and.push(this._externalFilter);
            return _filterQuery;
        }
        if (_filterQuery.$or) {
            _filterQuery = {
                $and: [_filterQuery, this._externalFilter],
            };
            return _filterQuery;
        }
        return _filterQuery;
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /**
     * Triggered when the user has selected rows in the grid
     * @param eventData.selectedRows {Array} - An array of strings representing the IDs of the selected rows
     * @private
     */
    _triggerUserSelectedRows(eventData) {
        this.emit("UserSelectedRows", eventData);
    }

    /**
     * Triggered when the user has double-clicked a row
     * @param eventData.selectedRow {String} - The ID of the row that has been double-clicked
     * @private
     */
    _triggerUserRowDoubleClicked(eventData) {
        this.emit("UserRowDoubleClicked", eventData);
    }

    /**
     * Triggered when the grid has been scrolled by the user
     * @param eventData.scrollTop {Number} - The pixel value of the distance the grid body has been scrolled from the top
     * @param eventData.scrollLeft {Number} - The pixel value of the distance the grid body has been scrolled from the left
     * @param eventData.event {Event} - The DOM scroll event
     * @private
     */
    _triggerUserScrolled(eventData) {
        this.emit("UserScrolled", eventData);
    }

    /**
     * Triggered when the user has sorted the grid
     * @param eventData.sortColumn {String} - The ID of the column that has been sorted
     * @param eventData.sort {String} - The direction ('asc' or 'desc') of the sort
     * @private
     */
    _triggerUserSorted(eventData) {
        this.emit("UserSorted", eventData);
    }

    /**
     * Triggered when the user has reordered the grid
     * @param eventData.column {String} - The ID of the column that has been reordered
     * @param eventData.columnOrder {Array} - An array of column IDs (strings) representing the new column order
     * @private
     */
    _triggerUserReordered(eventData) {
        this.emit("UserReordered", eventData);
    }

    /**
     * Triggered when something actionable inside of a grid row has sent an action event through the grid
     * @param eventData.rowId {String} - The ID of the row that triggered the action
     * @param eventData.eventData {Object} - Any data that was generated by the action event
     * @private
     */
    _triggerGridAction(eventData) {
        this.emit("GridAction", eventData);
    }

    /**
     * Triggered when the grid has been resized
     * @param eventData.event {Event} - Resize event
     * @param eventData.gridBodyWidth {Number} - The width in pixels of the grid body
     * @private
     */
    _triggerGridResized(event) {
        this.emit("GridResized", event);
    }

    /**
     * Triggered when the grid starts being busy
     * @param eventData.eventData {Object} - Any data that was generated by the action event
     * @private
     */
    _triggerGridBusyStart(eventData) {
        this.emit("GridBusyStart", eventData);
    }

    /**
     * Triggered when the grid stops being busy
     * @param eventData.eventData {Object} - Any data that was generated by the action event
     * @private
     */
    _triggerGridBusyEnd(eventData) {
        this.emit("GridBusyEnd", eventData);
    }

    _triggerColumnUserResized(eventData) {
        this.emit("ColumnUserResized", eventData);
    }

    _triggerBaseFilterChangeData(filterData) {
        this.emit("BasicSearchFilterChangeData", filterData);
    }

    _triggerAdvancedFilterChangeData(filterQuery) {
        this.emit("AdvancedSearchFilterChangeData", filterQuery);
    }

    _triggerColumnSettingChange(columnSettings) {
        this.emit("ColumnSettingChange", columnSettings);
    }

    /**
     * Triggered when Grouping state has been changed
     * @param {Object} groupingData
     * @param {string} [groupingData.groupBy = this._groupBy] - Group by column name
     * @param {string[]} [groupingData.expanded = []] - list of expanded groups
     * @private
     */
    _triggerGroupingChanged({
        groupBy = this._groupBy,
        expanded = [],
        prevColStatus = this._prevColStatus,
    }) {
        this.emit("GroupingChanged", {groupBy, expanded, prevColStatus});
    }
    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    setId(newVal) {
        this._id = newVal;
        this.emit("Id", this._id);
        return this;
    }

    getId() {
        return this._id;
    }

    getEntities() {
        return this._entities;
    }

    getEntity(key) {
        return this._entities[key];
    }

    getEntityOrder() {
        return this._pagingFeed.entityOrder;
    }

    getInitialColumnOrder(columnConfig) {
        return _.chain(columnConfig)
            .keys()
            .sortBy((colName) => _.get(columnConfig[colName], "priority"))
            .value();
    }

    setColumns(newVal, silent = false) {
        this._columns = newVal;
        this._initColumnOrder(this._columnOrderCache);
        this._updateColumnOrderCache();

        if (!silent) {
            this.emit("Columns", this._columns);
        }
        return this;
    }

    setColumnWidth(newVal, silent = false) {
        // newVal object: {column: string, width: number}
        if (!silent) {
            this.emit("ColumnWidth", newVal);
        }
    }

    getColumns() {
        return this._columns;
    }

    updateColumn(colJSON) {
        _.merge(this._columns, colJSON);
        //let colId = Object.keys(colJSON)[0];
        //this._columns[colId] = colJSON[colId];
        return this;
    }

    getColumnValuePath(colId) {
        return _.get(this._columns, [colId, "path"], colId);
    }
    getColumnDataType(colId) {
        return _.get(
            this._columnsTypeDataByFieldPath,
            [this.getColumnValuePath(colId), "dataType"],
            "String"
        );
    }

    /**
     * The type of the values held in a column, as opposed to how the column is presented.
     * A select column reports dataType "enum" while its values stay numbers or booleans.
     * @param colId {String} - column id
     * @returns {String} - the value's data type
     */
    getColumnValueDataType(colId) {
        const typeData = this._columnsTypeDataByFieldPath[this.getColumnValuePath(colId)];
        return _.get(typeData, "valueDataType") || _.get(typeData, "dataType", "String");
    }

    /**
     * Set locked columns in the grid
     * @param newVal {Array} - An array of column IDs representing the order of the locked columns
     * @param noEmit {Boolean} - If true, set the new value without emitting it
     * @returns {EntityGridComponent}
     */
    setLockedColumns(newVal, noEmit) {
        let columns = this._columns;
        for (let colId in columns) {
            columns[colId].locked = false;
        }
        newVal.forEach((colId) => {
            if (columns.hasOwnProperty(colId)) {
                columns[colId].locked = true;
            }
        });
        this._updateLockedColumnsCache();
        this._updateRightLockedColumnsCache();
        if (!noEmit) {
            this.emit("LockedColumns", this._lockedColumnsCache);
        }

        return this;
    }

    getLockedColumns() {
        return this._lockedColumnsCache;
    }

    getRightLockedColumns() {
        return this._lockedRightColumnsCache;
    }

    /**
     * Set the column order in the grid
     * @param newVal {Array} - An array of column IDs representing the column order
     * @param noEmit {Boolean} - If true, set the new value without emitting it
     * @returns {EntityGridComponent}
     */
    setColumnOrder(newVal = [], noEmit = false) {
        if (!newVal.length) {
            return this;
        }
        const columnsConfig = this._columns;
        newVal = newVal.filter(
            (colName) =>
                columnsConfig.hasOwnProperty(colName) && columnsConfig[colName].shown !== false
        );
        if (this._columnOrderCache.includes("grouping")) {
            //move grouping column to the first place
            newVal = [].concat(
                "grouping",
                newVal.filter((col) => col !== "grouping")
            );
        }
        _.forEach(columnsConfig, (colData, columnId) => {
            if (
                this._columnOrderCache.includes(columnId) &&
                columnsConfig[columnId].isGroupOnlyColumn
            ) {
                newVal = [].concat(
                    newVal.filter((col) => col !== columnId),
                    columnId
                );
            }
        });
        // IMPORTANT: can't change show property in SetColumns - it should be done from show/hide columns methods
        let firstWrong,
            previousFirstWrong = -1,
            that = this;
        this._updateColumnOrderCache();
        do {
            firstWrong = getFirstWrong();
            if (firstWrong !== -1) {
                if (previousFirstWrong === firstWrong) {
                    console.error(
                        "Something went wrong in setColumnOrder: current columns %o, current priorities %o, new order %o",
                        columnsConfig,
                        this._columnOrder,
                        newVal
                    );
                    break;
                }
                previousFirstWrong = firstWrong;

                if (!columnsConfig[newVal[firstWrong]]) {
                    newVal.splice(firstWrong, 1);
                    previousFirstWrong--;
                    continue;
                }
                let startOrder = this._columnOrder[this._columnOrderCache[firstWrong]];
                let finishOrder = this._columnOrder[newVal[firstWrong]];
                for (let colId in this._columnOrder) {
                    if (
                        this._columnOrder.hasOwnProperty(colId) &&
                        this._columnOrder[colId] >= startOrder &&
                        this._columnOrder[colId] < finishOrder
                    ) {
                        this._columnOrder[colId]++;
                    }
                }
                this._columnOrder[newVal[firstWrong]] = startOrder;
                this._updateColumnOrderCache();
            }
        } while (firstWrong !== -1);

        if (this._columnSettingsCmpnt) {
            while (
                this._columnOrderCache.length &&
                this._columnOrderCache[this._columnOrderCache.length - 1] !==
                    newVal[newVal.length - 1]
            ) {
                this._columns[
                    this._columnOrderCache[this._columnOrderCache.length - 1]
                ].shown = false;
                this._updateColumnOrderCache();
            }
        }

        if (noEmit !== true) {
            this.emit("ColumnOrder", this._columnOrderCache);
        }
        return this;

        function getFirstWrong() {
            for (let i = 0, lastGood = 0; i < newVal.length; i++) {
                while (
                    that._columns[that._columnOrderCache[lastGood]].hasOwnProperty("shown") &&
                    !that._columns[that._columnOrderCache[lastGood]].shown
                ) {
                    lastGood++;
                }
                if (newVal[i] === that._columnOrderCache[lastGood]) {
                    lastGood++;
                    continue;
                } else if (!that._columns[newVal[i]]) {
                    continue;
                } else if (
                    that._columns[newVal[i]].hasOwnProperty("shown") &&
                    !that._columns[newVal[i]].shown
                ) {
                    continue;
                } else {
                    return i;
                }
            }
            return -1;
        }
    }

    getColumnOrder() {
        return this._columnOrderCache;
    }

    getColumnPriority() {
        return this._columnOrder;
    }

    showColumn(columnId) {
        const userDataWidth = this._userGridData.getColWidth();
        this.emit("ShowColumn", columnId);
        if (Number(userDataWidth[columnId])) {
            this.setColumnWidth({
                column: columnId,
                width: userDataWidth[columnId],
            });
        }
        return this;
    }

    hideColumn(columnId) {
        this.emit("HideColumn", columnId);
        return this;
    }

    showGroupOnlyColumn() {
        const userDataWidth = this._userGridData.getColWidth();
        Object.keys(this._columns).map((groupingOnlyHeader) => {
            if (this._columns[groupingOnlyHeader].isGroupOnlyColumn) {
                this.emit("ShowColumn", groupingOnlyHeader);
                if (Number(userDataWidth[groupingOnlyHeader])) {
                    this.setColumnWidth({
                        column: groupingOnlyHeader,
                        width: userDataWidth[groupingOnlyHeader],
                    });
                }
            }
        });
        return Promise.resolve();
    }

    hideGroupOnlyColumn() {
        Object.keys(this._columns).map((groupingOnlyHeader) => {
            if (this._columns[groupingOnlyHeader].isGroupOnlyColumn) {
                this.emit("HideColumn", groupingOnlyHeader);
            }
        });
        return this;
    }

    setShowHeader(newVal) {
        this._showHeader = newVal;
        this.emit("ShowHeader", this._showHeader);
        return this;
    }

    getShowHeader() {
        return this._showHeader;
    }

    setSelectedRows(rowIdArray) {
        rowIdArray = rowIdArray || [];
        this._selectedRows = rowIdArray;
        this.emit("SelectedRows", rowIdArray);
        this.emit("UpdateLinesNumber", {selected: rowIdArray.length});
        this._triggerUserSelectedRows({selectedRows: rowIdArray});
        return this;
    }

    getSelectedRows() {
        return this._selectedRows;
    }

    setSingleSelect(newVal) {
        this._singleSelect = newVal;
        return this;
    }

    getSingleSelect() {
        return this._singleSelect;
    }

    setUserSorted(sortingData) {
        let sorting = sortingData;
        const {sortColumn} = sortingData;

        if (_.get(this._columns, [sortColumn, "isGroupingColumn"])) {
            sorting.sortColumn = this._groupBy;
        }
        if (this._groupBy && this._groupBy !== sortColumn) {
            //patch sorting according to the grouping value
            sorting = [{sortColumn: this._groupBy, sort: "asc"}].concat(sortingData);
        }

        if (!_.isEqual(this._sortingData, sorting)) {
            this._sortingData = sorting;
            this._triggerUserSorted(sorting);
        }
    }

    updateSorting() {
        const sortColumn = this._renderer._sortColumn;
        const sortDir = this._renderer._sort || "asd";
        const currentSorting = {};

        if (sortColumn) {
            currentSorting.sortColumn = sortColumn;
            currentSorting.sort = sortDir;
        }

        this.setUserSorted(currentSorting);
    }

    updateFiltering(silent = true) {
        this._onFilterDataChange(this._filterQuery, silent);
    }

    setBusyVeil(state, eventData) {
        return state ? this._triggerGridBusyStart(eventData) : this._triggerGridBusyEnd(eventData);
    }

    getTotalEntities() {
        return this._pagingFeed.totalEntities + (this.groups || []).length;
    }

    getOriginalTotalEntities() {
        return this._pagingFeed.totalEntities;
    }
    /**
     * method to group the grid by column
     * @param {string} groupBy - column to group by
     * @param {string[]|'ALL'} expanded - list of expanded groups. use 'ALL' to expand all groups
     * @returns {Promise<void>}
     */

    setGroupBy(groupBy, expanded = []) {
        this.setBusyVeil(true);
        const groupingColumn = "grouping";
        const lockedColumns = this._lockedColumnsCache;
        let newLockedList = lockedColumns.filter((col) => col !== groupingColumn),
            prevGroupBy = this._groupBy;
        this._prevColStatus = this._prevColStatus || {};
        groupBy = _.has(this._columns, groupBy) ? groupBy : "";
        if (groupBy) {
            this._prevColStatus[groupBy] = _.get(this._columns, [groupBy, "shown"], false);
        }

        if (!_.isEmpty(this._groupingFieldFromUrl)) {
            groupBy = this._groupingFieldFromUrl;
        }

        return this.getGroupValues(
            this.getColumnValuePath(groupBy),
            this.getColumnValueDataType(groupBy)
        )
            .then((groups = []) => {
                if (this._MAX_GROUPS && groups.length > MAX_GROUPS) {
                    const columnLabel = _.get(this._columns, [groupBy, "label"]);
                    let infoMessage = {
                        title: `Group by ${columnLabel}`,
                        message: `Grouping feature is only available on column with less than ${MAX_GROUPS} distinct values`,
                    };
                    return this._modalUserFeedback
                        .displayNotification(infoMessage.message, infoMessage)
                        .catch(_.noop) //catch 'userRejected'
                        .then(() =>
                            Promise.reject(
                                new Error(
                                    `Group by warning: ${columnLabel} column has more then ${MAX_GROUPS} distinct values`
                                )
                            )
                        );
                }
                return groups;
            })
            .then((groups) => {
                //filter list of expanded groups to be sure that groups contains expanded items
                expanded =
                    expanded === "ALL"
                        ? groups
                        : expanded.filter((expandedGroup) => groups.includes(expandedGroup));

                this.groups = groups;
                this._groupBy = groupBy;
                this.expandedGroups = expanded;

                newLockedList = [].concat(groupBy ? groupingColumn : [], newLockedList);
                this.updateSorting();
                this.updateFiltering();

                return this._pagingFeed.reestablish();
            })
            .then(() => {
                this.setLockedColumns(newLockedList, true);

                if (groupBy) {
                    this.hideColumn(groupBy);
                    this.showColumn("grouping");
                    this.showGroupOnlyColumn();
                    //newLockedList = [groupingColumn].concat(newLockedList);
                } else {
                    this.hideColumn("grouping");
                    this.hideGroupOnlyColumn();
                }

                this.setSelectedRows(null);
            })
            .catch((error) => {
                this._groupBy = undefined;
                this.groups = [];
                this.expandedGroups = [];
                this._logger.warn({error}, "Can't get Groups Values");
                if (groupBy) {
                    this.setGroupBy();
                }
                return Promise.reject(error);
            })
            .then(() => {
                //restore previous GroupBy column status
                if (this._prevColStatus[prevGroupBy]) {
                    this.showColumn(prevGroupBy);
                    this.showGroupOnlyColumn();
                }
                this._prevColStatus = _.omit(this._prevColStatus, prevGroupBy);

                if (!this._groupBy) {
                    this._prevColStatus = {};
                }
            })
            .then(() => this._updateColumnSettings())
            .then(() =>
                this._triggerGroupingChanged({
                    groupBy: this._groupBy,
                    expanded: this.expandedGroups,
                    prevColStatus: this._prevColStatus,
                })
            );
    }

    getGroupValues(fieldName, dataType) {
        return Promise.resolve()
            .then(() => {
                if (this._groupingValuesFeed) {
                    const systemPayload = _.pick(this.pagingFeed.getFilterData(), [
                        "$systemQuery",
                        "$schemeQuery",
                    ]);
                    let userQuery;
                    if (!_.isEmpty(this._externalFilter)) {
                        userQuery = {
                            $and: [this._filterQuery, this._externalFilter],
                        };
                    } else {
                        userQuery = this._filterQuery;
                    }
                    const groupingValuesFeedQuery = Object.assign(systemPayload, {
                        $userQuery: userQuery,
                    });

                    return this._groupingValuesFeed
                        .startFeed(fieldName, groupingValuesFeedQuery)
                        .then((feed) => feed.getValues())
                        .then((distinctValues) =>
                            this.convertDistinctValuesToDataTypedValues(dataType, distinctValues)
                        );
                } else if (this._pagingFeed.getDistinctValuesOfField) {
                    return this._pagingFeed.getDistinctValuesOfField(fieldName);
                } else {
                    return Promise.reject("Groups getter function is not provided");
                }
            })
            .then((groups) => this.sortGroupValues(groups));
    }

    convertDistinctValuesToDataTypedValues(dataType, distinctValues) {
        const convertToDataTyped = (dataType) => (value) => {
            switch (String(dataType).toLowerCase()) {
                case "boolean":
                    return boolConverter(value);
                case "number":
                    return numberConverter(value);
                case "enum":
                    return enumConverter(value);
                default:
                    return stringConverter(value);
            }

            function boolConverter(value) {
                switch (value) {
                    case "true":
                        return true;
                    case "false":
                        return false;
                    default:
                        return undefined;
                }
            }

            function numberConverter(value) {
                return value === "null" ? null : isNaN(value) ? value : Number(value);
            }

            function stringConverter(value) {
                return value === "undefined" ? undefined : value;
            }

            function enumConverter(value) {
                switch (value) {
                    case "null":
                        return null;
                    case "undefined":
                        return undefined;
                    default:
                        return value;
                }
            }
        };
        return distinctValues.map(convertToDataTyped(dataType));
    }

    sortGroupValues(groups = this.groups) {
        //the groups should be sorted the same way as devices
        const sortDirection =
            this._pagingFeed.getSort()[this.getColumnValuePath(this._groupBy)] || 1;

        // The replacement group ("Blank") stands in for empty/null/missing values, which the
        // backend sorts at an extreme — so it cannot be ordered alphabetically with real values.
        const replacementValue = _.get(this._groupingValuesFeed, "_replacementValue");
        const [undefinedGroup, regular, nullGroup, blankGroup] = groups.reduce(
            ([undefinedGroup, regular, _null, _blank], groupValue) => {
                if (!_.isNil(replacementValue) && groupValue === replacementValue) {
                    return [undefinedGroup, regular, _null, _blank.concat(groupValue)];
                }
                switch (groupValue) {
                    case null:
                        return [undefinedGroup, regular, _null.concat(groupValue), _blank];
                    case undefined:
                        return [undefinedGroup.concat(groupValue), regular, _null, _blank];
                    default:
                        return [undefinedGroup, regular.concat(groupValue), _null, _blank];
                }
            },
            [[], [], [], []]
        );

        regular.sort(function regularCompare(a, b) {
            let compareResult = 0;
            if (Number.isFinite(a) && Number.isFinite(b)) {
                compareResult = a - b;
            } else {
                compareResult = String(a).localeCompare(String(b), "en", {sensitivity: "accent"});
            }
            return compareResult * sortDirection;
        });

        return sortDirection > 0
            ? [].concat(blankGroup, undefinedGroup, regular, nullGroup)
            : [].concat(regular, blankGroup, nullGroup, undefinedGroup);
    }

    toggleGroup(group, silent) {
        const replacementValue =
            (this._groupingValuesFeed && _.get(this._groupingValuesFeed, "_replacementValue")) ||
            null;
        let blankSelected = false;
        let groupCollapse = false;
        if (group === replacementValue) {
            group = [replacementValue, null, undefined];
            groupCollapse = _.differenceWith(group, this.expandedGroups).length === 0;
            blankSelected = true;
        } else {
            groupCollapse = this.expandedGroups.includes(group);
        }

        if (groupCollapse) {
            if (!blankSelected) {
                this.expandedGroups = this.expandedGroups.filter((gr) => gr !== group);
            } else {
                this.expandedGroups = this.expandedGroups.filter((gr) => group.indexOf(gr) === -1);
            }
        } else {
            this.expandedGroups = this.expandedGroups.concat(group);
        }
        this.updateFiltering();

        if (!silent) {
            this._pagingFeed.feedUpdate();
            this._triggerGroupingChanged({expanded: this.expandedGroups});

            //update selection
            if (this.getSelectedRows().length && groupCollapse) {
                this.pagingFeed.fetchAllEntityIds().then(
                    (result) => {
                        const updatedSelection = this.getSelectedRows().filter((id) =>
                            result.includes(id)
                        );
                        this.setSelectedRows(updatedSelection);
                    },
                    (error) => this._logger.warn({error})
                );
            }
        }
    }

    createGroupingEntity(groupBy, rowKey, header, index = 0, expanded = false) {
        const title = _.get(
            this.getColumns(),
            [groupBy, "options", header, "title"],
            String(header)
        );
        const count =
            (this._groupingValuesFeed && this._groupingValuesFeed.getValueCount(header)) || 0;
        const groupByColumnData = this._columns[groupBy];
        const replacementValue =
            (this._groupingValuesFeed && _.get(this._groupingValuesFeed, "_replacementValue")) ||
            null;

        const GroupEntity = class extends EventEmitter {
            constructor(
                grid,
                {
                    rowKey,
                    title,
                    count,
                    groupBy,
                    header,
                    index,
                    expanded,
                    groupByColumnData,
                    replacementValue,
                }
            ) {
                super();
                this._grid = grid;
                this.id = rowKey;
                this.pagePos = index;
                this.groupingValue = header;
                this._replacementValue = replacementValue;
                this.groupBy = groupBy;
                this.expanded = expanded;
                this.properties = {
                    header: header,
                    title: title,
                    count: count,
                };
                this.isGroupHeader = true;
                this.groupByColumnData = groupByColumnData;
            }
            getProperty(prop) {
                return this[prop] || "";
            }
            getId() {
                return rowKey;
            }
            toggleExpand() {
                return this._grid.toggleGroup(this.groupingValue);
            }
            setValue(prop, value) {
                let property = {};
                if (_.isPlainObject(prop)) {
                    property = prop;
                } else if (_.isString(prop) && !_.isNil(value)) {
                    property = {
                        [prop]: value,
                    };
                }

                this.properties = Object.assign({}, this.properties, property);
                this.emit("PropertiesUpdated", this.properties, this);
            }
        };
        return new GroupEntity(this, {
            rowKey,
            title,
            count,
            groupBy,
            header,
            index,
            expanded,
            groupByColumnData,
            replacementValue,
        });
    }

    pagingFeedGetRangeSelection(endId, startId) {
        const rangeIsValid = new Promise((resolve, reject) => {
            const isUuid = (id) =>
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/.test(id);
            if (isUuid(endId) && isUuid(startId)) {
                resolve([endId, startId]);
            } else {
                reject([endId, startId]);
            }
        });
        return rangeIsValid.then((range) => this._pagingFeed.getRangeSelection(...range));
    }

    /**
     * Loads and scrolls the grid to the specific  entity row
     * @param {string} - entityId
     */
    loadAndScrollTo(entityId) {
        this.pagingFeed.once("RequestedEntityFound", (index) =>
            this.renderer.scrollToTheRow(index)
        );
        this.pagingFeed._requestEntity(entityId);
    }

    /**
     * Redraws UI rows
     */
    refreshView() {
        this._setEntities(this._pagingFeed.getPageEntities(this._pagingFeed.activePage));
    }

    refreshLinesNumber() {
        let totalEntities = this.pagingFeed.totalEntities;
        this.emit("UpdateLinesNumber", {total: totalEntities});
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _setEntities(newVal) {
        let selectedRows = this.getSelectedRows();
        this._entities = newVal;

        this.emit("Entities", this._entities);
        this.setSelectedRows(selectedRows);
        return this;
    }

    _addEntity(newEntity) {
        _.assignIn(this._entities, newEntity);

        // TODO: Update entity order?  Come back to once serve-side sorting is flushed out.

        this.emit("AddEntity", newEntity);
        return this;
    }

    _deleteEntity(entity) {
        let selectedRows = this.getSelectedRows(),
            newSelectedRows = [],
            selectedRowFound = false,
            entityId = entity.getId();
        this._entities = _.omit(this._entities, entityId);

        // TODO: Update entity order?  Come back to once serve-side sorting is flushed out.

        selectedRows.forEach((row) => {
            if (row === entityId) {
                selectedRowFound = true;
            } else {
                newSelectedRows.push(row);
            }
        });

        if (selectedRowFound === true) {
            this.setSelectedRows(newSelectedRows);
        }
        this.emit("DeleteEntity", entityId);
        return this;
    }

    _initColumnOrder(columnOrder) {
        if (!this._columnOrder || Object.keys(this._columnOrder).length === 0) {
            this._columnOrder = {};
            let counter = 0;
            columnOrder.forEach((colId) => {
                if (this._columns.hasOwnProperty(colId)) {
                    this._columnOrder[colId] = counter++;
                }
            });
            for (let colId in this._columns) {
                if (!this._columnOrder.hasOwnProperty(colId)) {
                    this._columnOrder[colId] = Object.keys(this._columnOrder).length;
                }
            }
        } else {
            for (let colId in this._columns) {
                if (!this._columnOrder.hasOwnProperty(colId)) {
                    this._columnOrder[colId] = Object.keys(this._columnOrder).length;
                }
            }
            for (let colId in this._columnOrder) {
                if (!this._columns[colId]) {
                    delete this._columnOrder[colId];
                }
            }
            Object.keys(this._columnOrder) // normalize values
                .sort((a, b) => this._columnOrder[a] - this._columnOrder[b])
                .forEach((colId, index) => {
                    this._columnOrder[colId] = index;
                });
        }
    }

    _updateColumnsVisibility(columnOrder = []) {
        const groupBy = this._groupBy;
        let columns = Object.assign({}, this.getColumns());
        if (columnOrder && columnOrder.length) {
            columnOrder = columnOrder.filter((columnId) => {
                return (
                    columns.hasOwnProperty(columnId) &&
                    !columns[columnId].isGroupingColumn &&
                    !columns[columnId].isGroupOnlyColumn
                );
            });

            _.forEach(columns, (colData, columnId) => {
                let showState = columns[columnId].shown !== false;

                if (columns[columnId].isGroupingColumn || columns[columnId].isGroupOnlyColumn) {
                    showState = showState && !!groupBy;
                }

                if (columnId === groupBy) {
                    showState = false;
                }

                if (this._columnSettingsCmpnt) {
                    showState = columnOrder.includes(columnId);
                }

                columns[columnId] = Object.assign({}, colData, {shown: showState});
            });
            this._columnOrderCache = columnOrder;
        }
        this.setColumns(columns, true);
        return this;
    }

    _updateColumnsWidth(storedWidthData) {
        Object.keys(storedWidthData).forEach((column) => {
            this.setColumnWidth({
                column: column,
                width: storedWidthData[column],
            });
        });
        return this;
    }

    _applySortingDataToColumns(columns, sortingData) {
        const sortingCriteria = Object.keys(sortingData)[0]; //get primary sorting criteria

        return Object.entries(columns).reduce((columns, colEntry) => {
            const colName = colEntry[0];
            const colData = colEntry[1];
            let sortProp = {sort: ""};
            if (sortingCriteria === _.get(colData, "path", colName)) {
                sortProp.sort = sortingData[sortingCriteria] === -1 ? "desc" : "asc";
            }
            columns[colName] = Object.assign({}, colData, sortProp);
            return columns;
        }, {});
    }

    _updateColumnOrderCache() {
        this._columnOrderCache = [];
        for (let colId in this._columnOrder) {
            if (!this._columns[colId].hasOwnProperty("shown") || this._columns[colId].shown) {
                this._columnOrderCache.push({
                    name: colId,
                    order: this._columnOrder[colId],
                });
            }
        }
        let columnOrderCache = this._columnOrderCache
            .sort((a, b) => a.order - b.order)
            .map((a) => a.name);
        if (columnOrderCache.includes("grouping")) {
            //move grouping column to the first place
            columnOrderCache = ["grouping"].concat(
                columnOrderCache.filter((col) => col !== "grouping")
            );
        }

        this._columnOrderCache = columnOrderCache;
        this._updateLockedColumnsCache();
        this._updateRightLockedColumnsCache();
    }

    _updateLockedColumnsCache() {
        this._lockedColumnsCache = this._columnOrderCache.filter(
            (colId) => this._columns[colId].locked
        );
    }
    _updateRightLockedColumnsCache() {
        this._lockedRightColumnsCache = this._columnOrderCache.filter(
            (colId) => this._columns[colId].isLockedRight
        );
    }
    _updateColumnSettings() {
        if (this._columnSettingsCmpnt) {
            let showColumnSettings = [];
            let columnConfig = this.getColumns();
            let columnPriority = this.getColumnPriority();
            columnConfig = _.omitBy(
                columnConfig,
                (col) => col.isGroupingColumn || col.isGroupOnlyColumn
            );
            for (let columnName in columnConfig) {
                if (columnConfig.hasOwnProperty(columnName)) {
                    let column = columnConfig[columnName];
                    showColumnSettings.push({
                        label: column.label,
                        value: columnName,
                        priority: columnPriority[columnName],
                        shown: !column.hasOwnProperty("shown") || column.shown,
                        locked: column.locked,
                        path: column.path,
                        isGroupingColumn: column.isGroupingColumn,
                        isGroupOnlyColumn: column.isGroupOnlyColumn,
                    });
                }
            }
            showColumnSettings.sort((a, b) => a.priority - b.priority);
            this._columnSettingsCmpnt.emit("UpdateColumnSettings", showColumnSettings);
        }
    }

    _clearFilter(filter, columns) {
        const flatFilterObject = this._flattenObject(filter);
        const checkedValues = [];
        Object.keys(flatFilterObject).forEach((rows) => {
            let rowPath = rows.substring(0, rows.lastIndexOf("$") - 1);
            let splitRowPath = rows
                .substring(0, rows.lastIndexOf("$") - 1)
                .substring(rows.substring(0, rows.lastIndexOf("$") - 1).lastIndexOf("_") + 1);
            let pathOfRowPath = rowPath.substring(0, rowPath.lastIndexOf("_"));
            if (
                _.isEmpty(
                    checkedValues.filter(
                        (paths) => paths.column === splitRowPath && paths.path === pathOfRowPath
                    )
                ) &&
                !Number.isInteger(splitRowPath) &&
                !splitRowPath.startsWith("$")
            ) {
                checkedValues.push({column: splitRowPath, path: pathOfRowPath});
                if (!this._doesPropertyExist(splitRowPath, columns)) {
                    _.omit(filter, rowPath.replace(/_/g, "."));

                    let parent = rowPath
                        .replace(/_/g, ".")
                        .substring(0, rowPath.replace(/_/g, ".").indexOf(splitRowPath) - 1)
                        .substring(
                            0,
                            rowPath
                                .replace(/_/g, ".")
                                .substring(0, rowPath.replace(/_/g, ".").indexOf(splitRowPath) - 1)
                                .lastIndexOf(".")
                        );
                    let arrayPop = rowPath
                        .replace(/_/g, ".")
                        .substring(0, rowPath.replace(/_/g, ".").indexOf(splitRowPath) - 1)
                        .substring(
                            rowPath
                                .replace(/_/g, ".")
                                .substring(0, rowPath.replace(/_/g, ".").indexOf(splitRowPath) - 1)
                                .lastIndexOf(".") + 1
                        );

                    if (!Number.isNaN(arrayPop) && !_.isEmpty(arrayPop)) {
                        arrayPop = parseInt(arrayPop);
                        _.get(filter, parent).splice(arrayPop, 1);

                        if (this._isDeepEmpty(filter)) {
                            filter = {};
                        }
                    }
                }
            }
        });

        return filter;
    }

    _isDeepEmpty(input) {
        if (_.isEmpty(input)) {
            return true;
        }
        if (typeof input === "object") {
            for (const item of Object.values(input)) {
                // if item is not undefined and is a primitive, return false
                // otherwise dig deeper
                if ((item !== undefined && typeof item !== "object") || !this._isDeepEmpty(item)) {
                    return false;
                }
            }
            return true;
        }
        return _.isEmpty(input);
    }

    _doesPropertyExist(property, columns) {
        if (columns.indexOf(property) > -1) {
            return true;
        }
        return false;
    }

    _flattenObject(ob) {
        let toReturn = {};

        for (let i in ob) {
            if (!ob.hasOwnProperty(i)) {
                continue;
            }

            if (typeof ob[i] === "object") {
                let flatObject = this._flattenObject(ob[i]);
                for (let x in flatObject) {
                    if (!flatObject.hasOwnProperty(x)) {
                        continue;
                    }

                    toReturn[i + "_" + x] = flatObject[x];
                }
            } else {
                toReturn[i] = ob[i];
            }
        }
        return toReturn;
    }

    _resetUrlParams() {
        let routeDetails = Steve.router._getCurrentRouteDetails();
        if (!_.isEmpty(routeDetails.exp)) {
            routeDetails.rd = {};
            delete routeDetails.exp;
        }
        if (routeDetails.rd && _.get(routeDetails.rd, "filter", false)) {
            delete routeDetails.rd.filter;
        }
        if (routeDetails.rd && _.get(routeDetails.rd, "selection", false)) {
            delete routeDetails.rd.selection;
        }
        if (routeDetails.rd && _.get(routeDetails.rd, "openEditor", false)) {
            delete routeDetails.rd.openEditor;
        }
        if (routeDetails.rd && _.get(routeDetails.rd, "groupingField", false)) {
            delete routeDetails.rd.groupingField;
        }
        if (routeDetails.rd && _.get(routeDetails.rd, "sort", false)) {
            delete routeDetails.rd.sort;
        }
        Steve.router.replaceRouteInfo(routeDetails);
    }

    /*******************************************/
    /* END PRIVATE API METHODS                 */
    /*******************************************/
}

module.exports = EntityGridComponent;
